import { Box, Breadcrumbs, Typography } from "@mui/material";
import { formatDateHumanReadable } from "utils/formatting";
import { useEffect, useState } from "react";
import { Link as RouterLink, useLocation, useParams } from "react-router-dom";
import useApi from "../../hooks/useGaneshApi";
import React from "react";
import { useError } from "../../contexts/ErrorContext";
import { SampleSingle_T } from "../../types/Sample";
import SchemaConfigTable from "../../components/Schema/SchemaConfigTable";

const SampleDetailPage: React.FC = () => {
  const { sampleId } = useParams<{ sampleId: string }>();
  const [loading, setLoading] = useState<boolean>(true);
  const [sampleMetadata, setSampleMetadata] = useState<SampleSingle_T>({});

  const { apiRequest } = useApi();
  const { showError } = useError();

  useEffect(() => {
    const fetchSampleData = async () => {
      try {
        const response = await apiRequest<SampleSingle_T>(
          `/sample/${sampleId}`,
          { method: "GET" }
        );
        if (response.data) {
          setSampleMetadata(response.data);
        }
      } catch (error) {
        console.error(error);
        showError(`Failed to fetch sample metadata for ${sampleId}.`);
      } finally {
        setLoading(false);
      }
    };

    setLoading(true);
    document.title = `Sample ${sampleId} - Details`;

    if (sampleId) {
      fetchSampleData();
    } else {
      setLoading(false);
      throw new Error("No sampleId provided");
    }
  }, [sampleId]);

  return (
    <Box>
      {loading ? (
        <Box sx={{ p: 4 }}>
          <Typography variant="h6" color="text.secondary">
            Loading sample details...
          </Typography>
        </Box>
      ) : (
        <Box sx={{ p: 4 }}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <RouterLink
              to="/customers"
              style={{ textDecoration: "none", color: "inherit" }}
            >
              Customers
            </RouterLink>
            <RouterLink
              to={`/customer/${sampleMetadata.customer_id}/samples`}
              style={{ textDecoration: "none", color: "inherit" }}
            >
              {sampleMetadata.customer_name} Samples
            </RouterLink>
            <Typography color="textPrimary">
              {sampleId}: {sampleMetadata.sample_name}
            </Typography>
          </Breadcrumbs>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              {" "}
              {sampleMetadata.sample_name} (ID: {sampleId})
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 4 }}>
            <Typography>
              <strong>Sales owner:</strong> {sampleMetadata.sales_owner}
            </Typography>
            <Typography>
              <strong>Ops owner:</strong> {sampleMetadata.ops_owner}
            </Typography>
            <Typography>
              <strong>Sample type:</strong> {sampleMetadata.sample_type}
            </Typography>
            <Typography>
              <strong>Total number of samples per evaluation:</strong>{" "}
              {sampleMetadata.total_number_of_samples_per_evaluation}
            </Typography>
            <Typography>
              <strong>Row count before post-export scrubs:</strong>{" "}
              {sampleMetadata.row_count}
            </Typography>
            <Typography>
              <strong>Boundary(ies):</strong> {sampleMetadata.boundaries}
            </Typography>
            <Typography>
              <strong>Special Categories:</strong>{" "}
              {sampleMetadata.special_categories}
            </Typography>
          </Box>

          <Box sx={{ height: 400 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Schema Configuration
            </Typography>
            <SchemaConfigTable datasetId={sampleId} datasetType="sample" />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SampleDetailPage;
